// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";
import useResizeObserver from "@react-hook/resize-observer";
import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { mdiAlert, mdiCheck, mdiUndo } from "@mdi/js";
import { Button, Tabs, Typography } from "antd";
import _debounce from "lodash/debounce";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { RootState } from "store/rootReducer";
import styled from "styled-components";

import useFeatureFlags from "hooks/useFeatureFlags";

import {
  deleteOrganizationMapDefault,
  getOrganizationMapDefault,
  setOrganizationMapDefault
} from "api/users";

import { EntityKind } from "models/entityKind";
import {
  DEFAULT_LATITUDE,
  DEFAULT_LONGITUDE,
  DEFAULT_ZOOM,
  MAPBOX_ACCESS_TOKEN
} from "models/mapDefaults";

import {
  COORDINATE_DECIMALS,
  ZOOM_DECIMALS,
  round
} from "components/map/utils/resolveJumpCamera";
import MapCenterPicker from "components/user-settings/components/defaults/MapCenterPicker";
import {
  ErrorContainer,
  SectionActionsContainer,
  SectionContainer,
  SectionHeading,
  SectionHeadingTitleContainer
} from "components/user-settings/components/defaults/shared";
import { useUserSettingsQueryKeys } from "components/user/queries/useUserSettingsDefaults/useUserSettingsDefaultsQuery";

import styles from "./OrganizationDefaults.module.scss";
import OrganizationFocusTileDefaults from "./OrganizationFocusTileDefaults";

const { Title } = Typography;

const MAP_CONTAINER_ID = "organization-map-defaults-mapbox";

const ORG_DEFAULT_MAP_CENTER_QUERY_KEY = "OrgDefaultMapCenter";

const FOCUS_TAB_KEY = "focus";
const MAP_TAB_KEY = "map";

type PendingMapCenter = {
  latitude?: number;
  longitude?: number;
  zoom?: number;
};

function getValidationError(value: PendingMapCenter): string | null {
  if (
    value?.latitude === undefined ||
    value?.latitude === null ||
    isNaN(value.latitude)
  ) {
    return "Latitude is required.";
  }
  if (value.latitude < -90 || value.latitude > 90) {
    return "Latitude must be between -90 and 90.";
  }
  if (
    value?.longitude === undefined ||
    value?.longitude === null ||
    isNaN(value.longitude)
  ) {
    return "Longitude is required.";
  }
  if (value.longitude < -180 || value.longitude > 180) {
    return "Longitude must be between -180 and 180.";
  }
  if (
    value.zoom !== undefined &&
    value.zoom !== null &&
    (isNaN(value.zoom) || value.zoom < 0 || value.zoom > 24)
  ) {
    return "Zoom must be between 0 and 24.";
  }
  return null;
}

const OrganizationMapDefaults = () => {
  // state from store
  const user = useSelector((state: RootState) => state.auth.user);
  const queryClient = useQueryClient();
  const { hasFeature } = useFeatureFlags();
  const hasBaseMidstream = hasFeature("Base Midstream");

  const organizationId = user?.organization?.id;

  // query
  const {
    data: orgMapDefault,
    isLoading,
    isError
  } = useQuery(
    [ORG_DEFAULT_MAP_CENTER_QUERY_KEY, organizationId],
    () => getOrganizationMapDefault(organizationId),
    { enabled: !!organizationId }
  );

  // "no organization default set" is the object being null/undefined — never a coordinate
  // being 0. See D2 — a saved (0, 0, 0) is a real value.
  const hasSavedDefault = orgMapDefault != null;

  // state
  const [pendingValue, setPendingValue] = useState<PendingMapCenter | undefined>(
    undefined
  );
  const [showSuccessCheck, setShowSuccessCheck] = useState(false);

  // Sync the picker from the saved value whenever it changes (initial load, refetch after
  // save/clear). When no org default is set, seed from the system default so the picker
  // always shows a usable starting point (EVA-32 — user overruled the earlier empty-state
  // design after review; see CONTEXT.md history).
  useEffect(() => {
    if (orgMapDefault != null) {
      setPendingValue({
        latitude: orgMapDefault.latitude,
        longitude: orgMapDefault.longitude,
        zoom: orgMapDefault.zoom
      });
    } else {
      setPendingValue({
        latitude: DEFAULT_LATITUDE,
        longitude: DEFAULT_LONGITUDE,
        zoom: DEFAULT_ZOOM
      });
    }
  }, [orgMapDefault]);

  // mutations
  const saveMutation = useMutation(
    (payload: { latitude: number; longitude: number; zoom?: number }) =>
      setOrganizationMapDefault(organizationId, payload),
    {
      onSuccess: () => {
        toast.success("Successfully saved organization default map location.");
        queryClient.invalidateQueries([ORG_DEFAULT_MAP_CENTER_QUERY_KEY, organizationId]);
        queryClient.invalidateQueries(useUserSettingsQueryKeys.userSettings);
        setShowSuccessCheck(true);
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (error: any) => {
        Sentry.captureException(error, {
          tags: { feature: "org-settings.save-map-default" },
          extra: { organizationId }
        });
        if (error.response?.data) {
          toast.error(`An error occurred. ${error.response.data}`);
        } else {
          toast.error("Unable to connect to network.");
        }
      }
    }
  );

  useEffect(() => {
    if (!showSuccessCheck) return;
    const timer = setTimeout(() => setShowSuccessCheck(false), 2000);
    return () => clearTimeout(timer);
  }, [showSuccessCheck]);

  const clearMutation = useMutation(() => deleteOrganizationMapDefault(organizationId), {
    onSuccess: () => {
      toast.success(
        "Reset the organization default map location — users now fall back to the system default."
      );
      queryClient.invalidateQueries([ORG_DEFAULT_MAP_CENTER_QUERY_KEY, organizationId]);
      queryClient.invalidateQueries(useUserSettingsQueryKeys.userSettings);
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      Sentry.captureException(error, {
        tags: { feature: "org-settings.clear-map-default" },
        extra: { organizationId }
      });
      if (error.response?.data) {
        toast.error(`An error occurred. ${error.response.data}`);
      } else {
        toast.error("Unable to connect to network.");
      }
    }
  });

  // Map refs
  const mapboxRef = useRef<mapboxgl.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapPaneRef = useRef<HTMLDivElement | null>(null);
  const isProgrammaticMove = useRef(false);
  const isUserInteracting = useRef(false);

  // Keep the canvas sized to its container — this panel lives inside the full-screen
  // org-settings overlay, so a window resize (or the overlay sizing its content after the
  // map is constructed) would otherwise leave Mapbox rendering at a stale canvas size.
  // Matches the personal panel's pattern (DefaultMapSettings.tsx).
  const debouncedResize = useMemo(
    () =>
      _debounce(() => {
        mapboxRef.current?.resize();
      }, 100),
    []
  );

  useEffect(() => {
    return () => {
      debouncedResize.cancel();
    };
  }, [debouncedResize]);

  // Observes the tab pane, not the map container: the hook reads the ref once on mount,
  // before loading has rendered the container. The pane is force-rendered, so it exists
  // then, and it also resizes when the hidden tab is first shown.
  useResizeObserver(mapPaneRef, debouncedResize);

  useEffect(() => {
    if (isLoading || !mapContainerRef.current || mapboxRef.current) return;

    // eslint-disable-next-line import/no-named-as-default-member
    mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

    // Derive the initial camera from the committed query result first, not from
    // pendingValue — see BLOCKING-2. `pendingValue` is still `undefined` in the same
    // commit that the sync effect (above) sets it, so a map built from it falls through
    // to the system default even when a saved default exists.
    const initialLat = orgMapDefault?.latitude ?? DEFAULT_LATITUDE;
    const initialLng = orgMapDefault?.longitude ?? DEFAULT_LONGITUDE;
    const initialZoom = orgMapDefault?.zoom ?? DEFAULT_ZOOM;

    // eslint-disable-next-line import/no-named-as-default-member
    mapboxRef.current = new mapboxgl.Map({
      container: MAP_CONTAINER_ID,
      style: "mapbox://styles/mapbox/streets-v11",
      center: [initialLng, initialLat],
      zoom: initialZoom
    });

    const handleMoveEnd = () => {
      if (!mapboxRef.current) return;

      if (isProgrammaticMove.current) {
        isProgrammaticMove.current = false;
        isUserInteracting.current = false;
        return;
      }

      const center = mapboxRef.current.getCenter();
      const zoom = mapboxRef.current.getZoom();

      // Recorded at the precision it is shown at, the same as a saved location.
      setPendingValue({
        latitude: round(center.lat, COORDINATE_DECIMALS),
        longitude: round(center.lng, COORDINATE_DECIMALS),
        zoom: round(zoom, ZOOM_DECIMALS)
      });

      isUserInteracting.current = false;
    };

    const handleMoveStart = () => {
      isUserInteracting.current = true;
    };

    mapboxRef.current.on("movestart", handleMoveStart);
    mapboxRef.current.on("moveend", handleMoveEnd);

    return () => {
      mapboxRef.current?.off("movestart", handleMoveStart);
      mapboxRef.current?.off("moveend", handleMoveEnd);
      mapboxRef.current?.remove();
      mapboxRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  // Re-center for the case where the query resolves (or changes, e.g. switching orgs, or a
  // Reset clears the org default) after the map has already been built at a stale camera.
  // Fly to the system default rather than bailing when orgMapDefault is null — the sync
  // effect above always reseeds pendingValue (and the inputs) to the system default in that
  // same state, so the camera must follow or the crosshair points at a value no input
  // reflects (see the reviewer's BUG note on the Reset path).
  useEffect(() => {
    // isUserInteracting: never yank the camera out from under an in-progress drag. A save's
    // invalidation can resolve with changed data mid-drag, and the fly's synthetic moveend
    // would consume isProgrammaticMove, so the admin's real drag would end against a camera
    // the app moved. Same guard flyToLocation uses.
    if (!mapboxRef.current || isUserInteracting.current) return;
    isProgrammaticMove.current = true;
    mapboxRef.current.flyTo({
      center: [
        orgMapDefault?.longitude ?? DEFAULT_LONGITUDE,
        orgMapDefault?.latitude ?? DEFAULT_LATITUDE
      ],
      zoom: orgMapDefault?.zoom ?? DEFAULT_ZOOM,
      duration: 0
    });
  }, [orgMapDefault]);

  // Debounced fly to location — prevents the map from flying on every keystroke, and
  // mirrors the personal map-defaults picker's isUserInteracting guard (D-review
  // NON-BLOCKING-8): a drag started while a fly is in flight must not be swallowed by
  // the isProgrammaticMove flag in handleMoveEnd.
  const flyToLocation = useMemo(
    () =>
      _debounce((lat: number, lng: number, zoom: number) => {
        if (!mapboxRef.current || isUserInteracting.current) return;
        isProgrammaticMove.current = true;
        mapboxRef.current.flyTo({ center: [lng, lat], zoom, duration: 500 });
      }, 600),
    []
  );

  useEffect(() => {
    return () => {
      flyToLocation.cancel();
    };
  }, [flyToLocation]);

  // handlers
  //
  // Each handler must guard its OWN incoming `value` before flying — antd's InputNumber
  // emits `null` on clear (see CONTEXT.md), and flyToLocation takes lat/lng/zoom
  // positionally, so an unguarded `null` gets coerced (`+null === 0`) into a garbage
  // camera move (equator for lat/lng, world view for zoom) even though validation
  // correctly blocks Save. Falsy-zero (0 is a legal latitude/longitude/zoom) means these
  // guards must use `!= null`, never truthiness.
  //
  // Zoom is optional end-to-end (backend `MapCenter.Zoom` is `double?`), so lat/lng
  // handlers must not require a non-null `next.zoom` to fly — that would permanently
  // freeze lat/lng tracking once a zoom-less default is persisted. Fall back to the
  // map's current zoom instead.
  const handleLatitudeChanged = useCallback(
    (value: number) => {
      const next = { ...pendingValue, latitude: value };
      setPendingValue(next);
      if (value == null || next.longitude == null) return;
      const zoom = next.zoom ?? mapboxRef.current?.getZoom() ?? DEFAULT_ZOOM;
      flyToLocation(value, next.longitude, zoom);
    },
    [pendingValue, flyToLocation]
  );

  const handleLongitudeChanged = useCallback(
    (value: number) => {
      const next = { ...pendingValue, longitude: value };
      setPendingValue(next);
      if (value == null || next.latitude == null) return;
      const zoom = next.zoom ?? mapboxRef.current?.getZoom() ?? DEFAULT_ZOOM;
      flyToLocation(next.latitude, value, zoom);
    },
    [pendingValue, flyToLocation]
  );

  const handleZoomChanged = useCallback(
    (value: number) => {
      const next = { ...pendingValue, zoom: value };
      setPendingValue(next);
      if (value == null || next.latitude == null || next.longitude == null) return;
      flyToLocation(next.latitude, next.longitude, value);
    },
    [pendingValue, flyToLocation]
  );

  const validationError = getValidationError(pendingValue ?? {});

  const handleSave = useCallback(() => {
    // Guards the mutation against firing with an undefined organizationId — unreachable
    // for a real admin (the GET is already gated by `enabled`), but closes the hazard
    // CONTEXT.md calls out rather than relying on that ordering.
    if (!organizationId || getValidationError(pendingValue ?? {})) return;

    saveMutation.mutate({
      latitude: pendingValue.latitude,
      longitude: pendingValue.longitude,
      zoom: pendingValue.zoom
    });
  }, [organizationId, pendingValue, saveMutation]);

  const handleClear = useCallback(() => {
    if (!organizationId) return;
    clearMutation.mutate();
  }, [organizationId, clearMutation]);

  if (isError) {
    return <RootContainer>Unable to connect to backend services.</RootContainer>;
  }

  return (
    <RootContainer>
      <div className="organizationAccountHeader">
        <Title level={3}>Defaults</Title>
      </div>
      <ContentContainer className={styles.organizationDefaults}>
        <Tabs
          data-testid="org-defaults-tabs"
          items={[
            {
              key: FOCUS_TAB_KEY,
              label: "Focus Defaults",
              children: (
                <div className={styles.focusTabContainer}>
                  <div className={styles.focusSection}>
                    <OrganizationFocusTileDefaults entityKind={EntityKind.Well} />
                  </div>
                  {hasBaseMidstream && (
                    <div className={styles.focusSection}>
                      <OrganizationFocusTileDefaults entityKind={EntityKind.Facility} />
                    </div>
                  )}
                </div>
              )
            },
            {
              key: MAP_TAB_KEY,
              label: "Map Defaults",
              // The map is built once, on mount, into this pane's container.
              forceRender: true,
              children: (
                <div ref={mapPaneRef} className={styles.mapTabContainer}>
                  <SectionContainer>
                    <SectionHeading>
                      <SectionHeadingTitleContainer>
                        Organization Default Map Location
                      </SectionHeadingTitleContainer>
                      {hasSavedDefault && (
                        <SectionActionsContainer>
                          <Button
                            type="text"
                            data-testid="reset-org-default-button"
                            loading={clearMutation.isLoading}
                            onClick={handleClear}
                            icon={<Icon path={mdiUndo} size={1} />}>
                            Reset
                          </Button>
                        </SectionActionsContainer>
                      )}
                    </SectionHeading>

                    {isLoading && (
                      <div data-testid="org-map-default-loading">Loading...</div>
                    )}

                    {!isLoading && (
                      <>
                        <MapCenterPicker
                          containerId={MAP_CONTAINER_ID}
                          containerRef={mapContainerRef}
                          value={pendingValue ?? {}}
                          onLatitudeChange={handleLatitudeChanged}
                          onLongitudeChange={handleLongitudeChanged}
                          onZoomChange={handleZoomChanged}
                          bottomActions={
                            <Button
                              type="primary"
                              size="large"
                              data-testid="save-org-default-button"
                              loading={saveMutation.isLoading}
                              disabled={!!validationError || !organizationId}
                              onClick={handleSave}
                              icon={
                                showSuccessCheck ? (
                                  <Icon path={mdiCheck} size={"16px"} />
                                ) : undefined
                              }>
                              {showSuccessCheck ? "Saved" : "Set Mid Point"}
                            </Button>
                          }
                        />

                        {validationError && (
                          <ErrorContainer data-testid="org-map-default-validation-error">
                            <Icon path={mdiAlert} size={1} /> {validationError}
                          </ErrorContainer>
                        )}
                      </>
                    )}
                  </SectionContainer>
                </div>
              )
            }
          ]}
        />
      </ContentContainer>
    </RootContainer>
  );
};

export default OrganizationMapDefaults;

const RootContainer = styled.div`
  gap: 10px;
  width: 100%;
  display: grid;
  text-align: left;
  overflow: auto;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: auto minmax(0, 1fr);
`;

const ContentContainer = styled.div`
  min-width: 768px;
  overflow: auto;
`;
